import { tenantConfigSchema, type TenantConfig } from '@/tenant/model/tenantConfig.ts'

/**
 * Tenant configs, loaded from `tenants/` at build time.
 *
 * Used **only** in development mode, where no backend is configured. In every
 * other mode the database is the runtime source and nothing here is read.
 *
 * Loading them from `tenants/*.json` rather than writing them in TypeScript is
 * what keeps studio names, prices and phone numbers out of `src`: the same
 * files the publishing pipeline consumes are the ones the development
 * fallback renders, so the two cannot drift.
 */
const rawConfigs: Record<string, unknown> = import.meta.glob('/tenants/*/business.json', {
  eager: true,
  import: 'default',
})

function slugFromPath(path: string): string {
  return path.split('/').at(-2) ?? ''
}

export const tenantConfigs: ReadonlyMap<string, TenantConfig> = new Map(
  Object.entries(rawConfigs).map(([path, raw]) => {
    // Parsed, not trusted: a malformed config should fail loudly at startup
    // rather than render half a studio.
    const config = tenantConfigSchema.parse(raw)
    return [slugFromPath(path), config]
  }),
)

export const tenantSlugs: readonly string[] = [...tenantConfigs.keys()].sort()

/**
 * The config for a slug, or the first available one.
 *
 * The fallback exists so opening `/` in development shows something; it is
 * never used when a backend is configured.
 */
export function demoTenantConfig(slug: string): TenantConfig | null {
  const exact = tenantConfigs.get(slug)
  if (exact !== undefined) return exact

  const first = tenantSlugs[0]
  return first === undefined ? null : (tenantConfigs.get(first) ?? null)
}
